const trapFocus = (container, returnFocusElement = null) => {
  if (!container) return;

  const focusableElements = container.querySelectorAll(
    `
    a[href], area[href], input:not([disabled]),
    select:not([disabled]), textarea:not([disabled]),
    button:not([disabled]), iframe, object, embed,
    [tabindex]:not([tabindex="-1"]), [contenteditable]
  `
  );

  if (focusableElements.length === 0) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    if (e.key === "Escape") {
      container.removeEventListener("keydown", handleKeyDown);
      if (returnFocusElement) returnFocusElement.focus();
      if (typeof FfEvents !== "undefined") {
        FfEvents.emit("a11y:focusTrapReleased", { container });
      }
    }
  };

  container.addEventListener("keydown", handleKeyDown);
  firstElement.focus();
};

class FfDrawer extends HTMLElement {
  constructor() {
    super();
    this.ffLastFocus = null;
    this.ffKeyDownCloses = true;
    this.ffIsOpen = false;

    this.ffInitialTouchDistance = null;

    this.ffPreventTouchMove = (e) => {
      if (e.target.closest(".ff-drawer")) {
        if (e.touches.length > 1) {
          if (window.visualViewport.scale > 1) {
            return;
          }

          e.preventDefault();
        }
        return;
      } else if (e.target == document.body) {
        if (document.body.classList.contains("ff-drawer-active")) {
          e.preventDefault();
        }
      }
    };

    document.body.addEventListener("touchmove", this.ffPreventTouchMove, {
      passive: false,
    });

    if (document.readyState === "complete") {
      this.ffInit();
    } else {
      window.addEventListener("load", () => {
        this.ffInit();
      });
    }
  }

  ffSetTogglers(target) {
    this.ffDrawerTogglers = target.querySelectorAll(
      `[data-ff-toggle-drawer-id='${this.id}']`
    );

    this.ffDrawerTogglers.forEach((drawerTogglerEl) => {
      drawerTogglerEl.addEventListener("click", this.ffToggle);
    });
  }

  ffToggle = (e) => {
    this.ffIsOpen ? this.ffClose() : this.ffOpen();
  };

  ffOpen = () => {
    const headerHeight = 0;
    this.style.top = headerHeight + "px";

    this.ffIsOpen = true;

    this.ffLastFocus = document.activeElement;

    document.body.classList.add("ff-overflow-hidden", "ff-drawer-active");
    document.body.dataset.ffDrawerActive = this.id;

    this.style.display = "";

    const drawerContent =
      this.ffDrawerContent?.querySelector(".ff-drawer_content");
    if (drawerContent) {
      drawerContent.style.height = "100%";
      this.ffOpenCloseAction = () => {
        this.style.setProperty(
          "--ff-drawer-content-height",
          drawerContent?.scrollHeight + "px"
        );
        drawerContent.style.height = "";
      };
    } else {
      this.ffOpenCloseAction = () => {};
    }

    if (headerHeight > 0) {
      this.style.height = `calc(100dvh - ${headerHeight}px`;
      this.style.top = headerHeight;
    } else {
      // If header is not fixed or sticky and is not visible cause of the scroll, set the drawer to the full height of the viewport
      this.style.height = "100dvh";
      this.style.top = "0px";
    }

    trapFocus(this, this.ffLastFocus);

    requestAnimationFrame(() => {
      this.classList.add("ff-drawer-open");
    });
  };

  ffClose = () => {
    this.ffIsOpen = false;

    this.ffOpenCloseAction = () => {
      this.style.display = "none";
      document.body.classList.remove("ff-overflow-hidden", "ff-drawer-active");
      document.body.dataset.ffDrawerActive = "";
    };

    this.classList.remove("ff-drawer-open");
  };

  ffInit = (onlyContents = false) => {
    if (onlyContents) {
      this.ffSetTogglers(this);
    } else {
      this.ffSetTogglers(document.body);

      this.addEventListener("transitionstart", (e) => {
        if (e.target === this.querySelector(".ff-drawer-slide--js")) {
          this.style.pointerEvents = "none";
          this.style.touchAction = "none";
        }
      });

      this.addEventListener("transitionend", (e) => {
        if (e.target === this.querySelector(".ff-drawer-slide--js")) {
          this.style.pointerEvents = "auto";
          this.style.touchAction = "auto";
          if (this.ffIsOpen == false) {
            this.style.display = "none";

            document.body.classList.remove(
              "ff-overflow-hidden",
              "ff-drawer-active"
            );
            document.body.dataset.ffDrawerActive = "";
          } else {
            this.ffKeyDownCloses = false;
          }
        }
      });

      this.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          this.ffClose();
        }
      });
    }
  };
}

customElements.define("ff-drawer", FfDrawer);
